import { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, ChevronLeft, ChevronRight, Trash2, ZoomIn, X, ImageIcon, Upload, Eye, EyeOff, Lock } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { ProgressPhotosCalendarPicker } from './ProgressPhotosCalendarPicker';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { toastError } from '@/lib/toastError';
import { addToPhotoQueue, MAX_QUEUED_PHOTOS } from '@/lib/offlineStorage';
import { ResilientImage } from '@/components/ResilientImage';
import { useReconnectRefetch } from '@/hooks/useReconnectRefetch';

import poseFront from '@/assets/pose-front.png';
import poseLeft from '@/assets/pose-left.png';
import poseRight from '@/assets/pose-right.png';
import poseBack from '@/assets/pose-back.png';

type PoseType = 'front' | 'left' | 'right' | 'back';

interface PhotoRecord {
  id: string;
  pose_type: PoseType;
  photo_url: string;
  storage_path: string;
  photo_month: string;
}

const POSES: { type: PoseType; label: string; guide: string }[] = [
  { type: 'front', label: 'Frente', guide: poseFront },
  { type: 'left', label: 'Perfil Izq.', guide: poseLeft },
  { type: 'right', label: 'Perfil Der.', guide: poseRight },
  { type: 'back', label: 'Espalda', guide: poseBack },
];

function getMonthLabel(monthStr: string) {
  const [y, m] = monthStr.split('-');
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${months[parseInt(m, 10) - 1]} ${y}`;
}

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// Build the list of last 12 months + current month (oldest first)
function buildLast12Months(): string[] {
  const months: string[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
}

interface Props {
  activeUser: string;
  viewingUserId?: string | null;
}

export function ProgressPhotosSection({ activeUser, viewingUserId }: Props) {
  const isCoachView = Boolean(viewingUserId);
  const [photos, setPhotos] = useState<PhotoRecord[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [availableMonths, setAvailableMonths] = useState<string[]>(buildLast12Months());
  const [uploading, setUploading] = useState<PoseType | null>(null);
  const [showPickerFor, setShowPickerFor] = useState<PoseType | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Storage key namespaced by the user being viewed so the coach's hide
  // preference for Joel doesn't affect when they later open Alexa's panel.
  // Falls back to activeUser for client view.
  const hideStorageKey = `photosHidden:${viewingUserId || activeUser || 'self'}`;
  const [photosHidden, setPhotosHidden] = useState<boolean>(() => {
    try {
      return localStorage.getItem(hideStorageKey) === 'true';
    } catch {
      return false;
    }
  });

  // Re-read preference when switching context (e.g. coach changes client)
  useEffect(() => {
    try {
      setPhotosHidden(localStorage.getItem(hideStorageKey) === 'true');
    } catch {
      setPhotosHidden(false);
    }
  }, [hideStorageKey]);
  const fileInputRefs = useRef<Record<PoseType, HTMLInputElement | null>>({
    front: null, left: null, right: null, back: null,
  });

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const targetUserId = viewingUserId || user.id;

      // Coach view: don't filter by profile_name (see ALL client photos
      // regardless of which profile they were uploaded under)
      let query = supabase
        .from('progress_photos')
        .select('*')
        .eq('user_id', targetUserId)
        .order('photo_month', { ascending: true });

      if (!viewingUserId) {
        query = query.eq('profile_name', activeUser);
      }

      const { data } = await query;

      if (data && data.length > 0) {
        // Generate fresh signed URLs for all photos
        const paths = (data as any[]).map(r => r.storage_path);
        const { data: signedUrls } = await supabase.storage
          .from('progress-photos')
          .createSignedUrls(paths, 60 * 60 * 24); // 24 hours

        const urlMap: Record<string, string> = {};
        (signedUrls || []).forEach(s => { if (s.signedUrl) urlMap[s.path!] = s.signedUrl; });

        const records = (data as any[]).map(r => ({
          id: r.id,
          pose_type: r.pose_type as PoseType,
          photo_url: urlMap[r.storage_path] || r.photo_url,
          storage_path: r.storage_path,
          photo_month: r.photo_month,
        }));
        setPhotos(records);

        // Always include last 12 months + current, plus any older month
        // that already has photos (so the client can scroll back further
        // if they have a longer history).
        const base = new Set(buildLast12Months());
        records.forEach(r => base.add(r.photo_month));
        const months = [...base].sort();
        setAvailableMonths(months);
        if (!months.includes(selectedMonth)) setSelectedMonth(getCurrentMonth());
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [activeUser, selectedMonth]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  // Refetch al volver la conexión: las fotos del cliente se cargan
  // con signed URLs que requieren red. Si la app abrió offline,
  // `photos` queda vacío y se ven solo las silhouettes grises de
  // "subir foto". Al reconectar, refetcheamos los registros de la
  // BD + generamos signed URLs frescas → las fotos reales aparecen
  // sin que el cliente tenga que cerrar y abrir la app.
  useReconnectRefetch(fetchPhotos);

  const monthPhotos = photos.filter(p => p.photo_month === selectedMonth);

  const handleUpload = async (poseType: PoseType, file: File) => {
    setUploading(poseType);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No auth');

      const ext = file.name.split('.').pop() || 'jpg';

      // OFFLINE PATH: queue the photo locally so it uploads when we reconnect.
      // Without this, taking progress photos at the gym (where signal is bad)
      // fails silently and the photo is lost.
      if (!navigator.onLine) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const queued = addToPhotoQueue({
          userId: user.id,
          profileName: activeUser,
          photoMonth: selectedMonth,
          poseType,
          base64,
          contentType: file.type || 'image/jpeg',
          ext,
        });
        if (queued) {
          toast.success('Foto guardada — se sincronizará cuando vuelvas a tener señal');
          // Optimistic preview using a local data URL
          const localUrl = `data:${file.type};base64,${base64}`;
          setPhotos(prev => {
            const filtered = prev.filter(
              p => !(p.photo_month === selectedMonth && p.pose_type === poseType)
            );
            return [
              ...filtered,
              {
                id: `local-${Date.now()}`,
                pose_type: poseType,
                photo_url: localUrl,
                storage_path: `pending/${selectedMonth}/${poseType}`,
                photo_month: selectedMonth,
              },
            ];
          });
        } else {
          toast.error(`Sin espacio para más fotos offline (máx ${MAX_QUEUED_PHOTOS}). Conéctate para subir las pendientes.`);
        }
        return;
      }

      // ONLINE PATH (original behavior)
      const path = `${user.id}/${selectedMonth}/${poseType}.${ext}`;
      const existing = monthPhotos.find(p => p.pose_type === poseType);
      if (existing && !existing.id.startsWith('local-')) {
        await supabase.storage.from('progress-photos').remove([existing.storage_path]);
        await supabase.from('progress_photos').delete().eq('id', existing.id);
      }

      const { error: uploadError } = await supabase.storage
        .from('progress-photos')
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data: signedData, error: signedError } = await supabase.storage
        .from('progress-photos')
        .createSignedUrl(path, 60 * 60 * 24 * 30);

      if (signedError) throw signedError;

      const { error: insertError } = await supabase.from('progress_photos').insert({
        user_id: user.id,
        profile_name: activeUser,
        photo_month: selectedMonth,
        pose_type: poseType,
        photo_url: signedData.signedUrl,
        storage_path: path,
      });

      if (insertError) throw insertError;

      toast.success('Foto subida correctamente');
      fetchPhotos();
    } catch (err: any) {
      // If the network died mid-upload, fall back to offline queue
      if (!navigator.onLine) {
        toast.warning('Conexión perdida — la foto se sincronizará automáticamente al reconectar.');
      } else {
        toastError('Error al subir foto: ' + (err.message || 'Intenta de nuevo'), {
          section: 'progress-photos-upload',
          extra: {
            errorMessage: err?.message,
            stack: err?.stack,
          },
        });
      }
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (photo: PhotoRecord) => {
    try {
      await supabase.storage.from('progress-photos').remove([photo.storage_path]);
      await supabase.from('progress_photos').delete().eq('id', photo.id);
      toast.success('Foto eliminada');
      fetchPhotos();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const navigateMonth = (dir: -1 | 1) => {
    const idx = availableMonths.indexOf(selectedMonth);
    const newIdx = idx + dir;
    // Never go past current month into the future, never past oldest available.
    if (newIdx < 0 || newIdx >= availableMonths.length) return;
    const target = availableMonths[newIdx];
    if (target > getCurrentMonth()) return;
    setSelectedMonth(target);
  };

  const monthIdx = availableMonths.indexOf(selectedMonth);
  const isCurrentMonth = selectedMonth >= getCurrentMonth();

  return (
    <section id="progress-photos" className="scroll-mt-32 animate-fade-in">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <SectionHeader
            title="Tu Progreso Visual"
            subtitle="Compara tu transformación mes a mes"
            icon={Camera}
            helpId="fotos-progreso"
          />
        </div>
        <button
          onClick={() => {
            setPhotosHidden(prev => {
              const next = !prev;
              try { localStorage.setItem(hideStorageKey, String(next)); } catch { /* storage disabled */ }
              toast(next ? '🔒 Fotos ocultas por privacidad' : '👁️ Fotos visibles');
              return next;
            });
          }}
          className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-300 hover:scale-105 shadow-lg text-xs font-bold uppercase tracking-wider ${
            photosHidden
              ? 'bg-destructive text-destructive-foreground hover:shadow-destructive/30'
              : 'bg-primary text-primary-foreground hover:shadow-primary/30'
          }`}
          title={photosHidden ? 'Mostrar fotos' : 'Ocultar fotos'}
        >
          {photosHidden ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          {photosHidden ? 'Mostrar' : 'Ocultar'}
        </button>
      </div>

      {/* Month navigator */}
      <div className="flex items-center justify-center gap-3 mb-8">
        <button
          onClick={() => navigateMonth(-1)}
          disabled={monthIdx <= 0}
          className="p-2 rounded-lg border border-border bg-card hover:bg-accent/10 disabled:opacity-30 transition-colors"
          aria-label="Mes anterior"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-lg font-bold uppercase tracking-wider min-w-[120px] text-center">
          {getMonthLabel(selectedMonth)}
        </span>
        <ProgressPhotosCalendarPicker
          selectedMonth={selectedMonth}
          photos={photos.map(p => ({ photo_month: p.photo_month, pose_type: p.pose_type }))}
          onSelect={(m) => setSelectedMonth(m)}
        />
        <button
          onClick={() => navigateMonth(1)}
          disabled={isCurrentMonth}
          className="p-2 rounded-lg border border-border bg-card hover:bg-accent/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title={isCurrentMonth ? 'No puedes subir fotos de meses futuros' : 'Mes siguiente'}
          aria-label="Mes siguiente"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Instruction */}
      <p className="text-center text-sm text-muted-foreground mb-6">
        Toca cada cuadro para subir tu foto en esa pose. Sigue la silueta como guía.
      </p>

      {/* 4 photo slots */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {POSES.map(({ type, label, guide }) => {
          const photo = monthPhotos.find(p => p.pose_type === type);
          const isUploading = uploading === type;

          return (
            <div key={type} className="flex flex-col items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                {label}
              </span>

               <div
                className="relative w-full aspect-[3/4] rounded-xl border-2 border-dashed border-border bg-card/50 overflow-hidden cursor-pointer group hover:border-accent/50 transition-all"
                onClick={() => {
                  if (photosHidden) return;
                  if (photo) {
                    setPreviewUrl(photo.photo_url);
                  } else if (!isCoachView) {
                    setShowPickerFor(showPickerFor === type ? null : type);
                  }
                }}
              >
                {photosHidden ? (
                  <div className="flex flex-col items-center justify-center h-full bg-muted/80 backdrop-blur-sm transition-all duration-300">
                    <Lock className="w-8 h-8 text-muted-foreground mb-2" />
                    <span className="text-xs font-bold text-muted-foreground text-center px-2">Fotos ocultas</span>
                    <span className="text-[10px] text-muted-foreground/70 text-center px-2">Presiona el ojo para mostrar</span>
                  </div>
                ) : photo ? (
                  <>
                    {/* ResilientImage en vez de <img> directo: si la
                     *  app abrió offline las fotos del cliente desde
                     *  Supabase storage no estaban en cache, fallaban
                     *  silenciosamente y al reconectar no se
                     *  reintentaban. Ahora se reintentan al evento
                     *  iftmx:reconnected y muestran indicador
                     *  "sin conexión" mientras estén caídas. */}
                    <ResilientImage
                      src={photo.photo_url}
                      alt={`Progreso ${label}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      fallback={
                        <div className="w-full h-full flex items-center justify-center bg-muted/30 text-muted-foreground/60">
                          <ImageIcon className="w-8 h-8" />
                        </div>
                      }
                    />
                    {/* Overlay on hover/tap */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); setPreviewUrl(photo.photo_url); }}
                        className="p-2 rounded-full bg-accent/20 hover:bg-accent/40 transition-colors"
                      >
                        <ZoomIn className="w-5 h-5 text-accent-foreground" />
                      </button>
                      {!isCoachView && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); fileInputRefs.current[type]?.click(); }}
                            className="p-2 rounded-full bg-accent/20 hover:bg-accent/40 transition-colors"
                            title="Tomar foto"
                          >
                            <Camera className="w-5 h-5 text-accent-foreground" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); document.getElementById(`gallery-${type}`)?.click(); }}
                            className="p-2 rounded-full bg-accent/20 hover:bg-accent/40 transition-colors"
                            title="Subir de galería"
                          >
                            <Upload className="w-5 h-5 text-accent-foreground" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(photo); }}
                            className="p-2 rounded-full bg-destructive/20 hover:bg-destructive/40 transition-colors"
                          >
                            <Trash2 className="w-5 h-5 text-destructive" />
                          </button>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full p-4 gap-2">
                    {isUploading ? (
                      <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
                    ) : (
                      <>
                        <img
                          src={guide}
                          alt={`Guía pose ${label}`}
                          className="w-3/5 max-h-[50%] object-contain opacity-25"
                          loading="lazy"
                        />
                        {isCoachView ? (
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <ImageIcon className="w-4 h-4" />
                            <span className="text-[10px] font-semibold uppercase">Sin foto</span>
                          </div>
                        ) : showPickerFor === type ? (
                          <div className="flex flex-col gap-2 w-full px-2 animate-in fade-in duration-200">
                            <button
                              onClick={(e) => { e.stopPropagation(); fileInputRefs.current[type]?.click(); setShowPickerFor(null); }}
                              className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-accent/15 hover:bg-accent/25 text-accent-foreground transition-colors text-[10px] font-semibold uppercase"
                            >
                              <Camera className="w-3.5 h-3.5" />
                              Tomar foto
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); document.getElementById(`gallery-${type}`)?.click(); setShowPickerFor(null); }}
                              className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-accent/15 hover:bg-accent/25 text-accent-foreground transition-colors text-[10px] font-semibold uppercase"
                            >
                              <Upload className="w-3.5 h-3.5" />
                              Galería
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <ImageIcon className="w-4 h-4" />
                            <span className="text-[10px] font-semibold uppercase">Subir foto</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Camera capture */}
              <input
                ref={el => { fileInputRefs.current[type] = el; }}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(type, file);
                  e.target.value = '';
                }}
              />
              {/* Gallery pick */}
              <input
                id={`gallery-${type}`}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(type, file);
                  e.target.value = '';
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Tips */}
      <div className="mt-6 p-4 rounded-xl bg-card border border-border">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <span className="font-bold text-foreground">💡 Tips para mejores fotos:</span>{' '}
          Usa la misma iluminación y fondo cada mes. Tómalas a la misma hora (idealmente en la mañana). Usa ropa ajustada o mínima para ver los cambios.
        </p>
      </div>

      {/* Fullscreen preview modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setPreviewUrl(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-card/20 hover:bg-card/40 transition-colors"
            onClick={() => setPreviewUrl(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <ResilientImage
            src={previewUrl}
            alt="Vista completa"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            fallback={
              <div className="text-white p-8 text-center bg-white/5 rounded-lg max-w-md">
                No se pudo cargar la foto. Vuelve a intentarlo cuando tengas conexión.
              </div>
            }
          />
        </div>
      )}
    </section>
  );
}
